import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronRight,
  TrendingUp,
  Briefcase,
  Ticket,
  AlertTriangle,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { AutoFaucet, WatchAds, ErrorBoundary } from '../components';
import { useAppStore } from '../store/useAppStore';
import { useConfigStore } from '../store/useConfigStore';

// Frontend logger that sends to backend for Railway monitoring
const frontendLogger = {
  log: (level: 'info' | 'warn' | 'error', message: string, data?: Record<string, unknown>) => {
    const logEntry = {
      level,
      message,
      data,
      page: 'EarnPage',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Always log to console
    const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    consoleMethod(`[EarnPage] ${message}`, data || '');

    // Send to backend for Railway monitoring (non-blocking)
    const API_URL = import.meta.env.VITE_API_URL || '/api';
    fetch(`${API_URL}/logs/frontend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logEntry),
    }).catch(() => {
      // Silent fail - don't disrupt user experience
    });
  },
  info: (msg: string, data?: Record<string, unknown>) => frontendLogger.log('info', msg, data),
  warn: (msg: string, data?: Record<string, unknown>) => frontendLogger.log('warn', msg, data),
  error: (msg: string, data?: Record<string, unknown>) => frontendLogger.log('error', msg, data),
};

export function EarnPage() {
  const [pageError, setPageError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get user and config state
  const user = useAppStore((state) => state.user);
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const { adsEnabled, maintenanceMode, isLoading: configLoading, fetchConfig } = useConfigStore();

  // Log page mount and check dependencies
  useEffect(() => {
    frontendLogger.info('EarnPage mounted', {
      isAuthenticated,
      hasUser: !!user,
      userId: user?.telegramId,
      adsEnabled,
      maintenanceMode,
      configLoading,
    });

    // Validate dependencies
    const checkDependencies = async () => {
      try {
        // Check if user is authenticated
        if (!isAuthenticated || !user) {
          frontendLogger.warn('EarnPage: User not authenticated', { isAuthenticated, hasUser: !!user });
          setPageError('Please log in to access this page');
          setIsLoading(false);
          return;
        }

        // Check if config is loaded
        if (configLoading) {
          frontendLogger.info('EarnPage: Waiting for config to load');
          // Wait for config with timeout
          const timeout = setTimeout(() => {
            if (configLoading) {
              frontendLogger.error('EarnPage: Config load timeout');
              setPageError('Failed to load configuration. Please refresh the page.');
            }
          }, 10000);
          
          return () => clearTimeout(timeout);
        }

        // Force refresh config if needed
        await fetchConfig();
        
        frontendLogger.info('EarnPage: Dependencies validated successfully', {
          adsEnabled,
          maintenanceMode,
        });
        
        setIsLoading(false);
      } catch (error) {
        frontendLogger.error('EarnPage: Dependency check failed', {
          error: error instanceof Error ? error.message : String(error),
        });
        setPageError('Failed to load page data. Please try again.');
        setIsLoading(false);
      }
    };

    checkDependencies();
  }, [isAuthenticated, user, configLoading, adsEnabled, maintenanceMode, fetchConfig]);

  // Handle component errors
  const handleComponentError = (componentName: string, error: Error) => {
    frontendLogger.error(`Component crashed: ${componentName}`, {
      error: error.message,
      stack: error.stack,
      userId: user?.telegramId,
    });
  };

  // Handle retry
  const handleRetry = () => {
    frontendLogger.info('EarnPage: User triggered retry');
    setPageError(null);
    setIsLoading(true);
    fetchConfig(true).then(() => {
      setIsLoading(false);
    }).catch((err) => {
      frontendLogger.error('EarnPage: Retry failed', { error: err.message });
      setPageError('Retry failed. Please try again later.');
      setIsLoading(false);
    });
  };

  // Show loading state
  if (isLoading || configLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-white/60 text-sm">Loading earn options...</p>
      </div>
    );
  }

  // Show error state
  if (pageError) {
    return (
      <div className="card p-6 bg-red-500/10 border border-red-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-400 mb-2">Unable to Load</h3>
            <p className="text-sm text-white/60 mb-4">{pageError}</p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show maintenance mode message
  if (maintenanceMode) {
    frontendLogger.info('EarnPage: Showing maintenance mode');
    return (
      <div className="card p-6 bg-yellow-500/10 border border-yellow-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-yellow-400 mb-2">Under Maintenance</h3>
            <p className="text-sm text-white/60">
              The earn features are currently under maintenance. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Auto Faucet component with error boundary */}
      <ErrorBoundary 
        componentName="AutoFaucet"
        onError={(error) => handleComponentError('AutoFaucet', error)}
      >
        <AutoFaucet />
      </ErrorBoundary>

      {/* Watch Ads component with error boundary */}
      <ErrorBoundary 
        componentName="WatchAds"
        onError={(error) => handleComponentError('WatchAds', error)}
      >
        <WatchAds />
      </ErrorBoundary>

      {/* Quick Links */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-4"
      >
        <h3 className="font-bold mb-3">More Ways to Earn</h3>
        <div className="space-y-2">
          <Link 
            to="/offers" 
            className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium">Complete Tasks</p>
                <p className="text-xs text-white/50">Earn big rewards</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/40" />
          </Link>
          <Link 
            to="/redeem" 
            className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium">Redeem Code</p>
                <p className="text-xs text-white/50">Use promo codes</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/40" />
          </Link>
        </div>
      </motion.div>

      {/* Tip card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card p-4 bg-gradient-to-r from-primary/20 to-primary-dark/20 border border-primary/30"
      >
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1 text-sm">Pro Tip</p>
            <p className="text-white/60 text-xs">
              Combine auto faucet with watching videos for maximum earnings!
              Higher levels unlock better rewards.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
